import { createConsoleSubmitter } from '@/lib/submitter/console-adapter';
import { NotImplementedError, type FormSubmitter } from '@/lib/submitter/types';
import type { SubmitterId } from '@/types/types';

export type { SubmitterId } from '@/types/types';

const SUPPORTED_IDS = new Set<SubmitterId>(['console', 'telegram', 'bitrix', 'email']);

function notImplemented(id: SubmitterId): FormSubmitter {
  return {
    id,
    async submit() {
      throw new NotImplementedError(
        `[submitter] adapter "${id}" is not implemented yet. ` +
          'Real transports (Telegram / Bitrix24 / email) will be added by the Ops/backend phase.',
      );
    },
  };
}

function resolveId(raw: string | undefined): SubmitterId {
  if (!raw) return 'console';
  if (SUPPORTED_IDS.has(raw as SubmitterId)) return raw as SubmitterId;
  return 'console';
}

/**
 * Защита от случайной выкатки console-адаптера в прод. Явный opt-in
 * через `VITE_ALLOW_CONSOLE_SUBMITTER=1` (для staging). Иначе — throw
 * при сборке production-кода.
 *
 * Вынесено в отдельную функцию для тестируемости.
 */
export function enforceProductionGuard(options: {
  readonly id: SubmitterId;
  readonly isProd: boolean;
  readonly allowConsole: boolean;
}): void {
  if (options.isProd && options.id === 'console' && !options.allowConsole) {
    throw new Error(
      '[submitter] console adapter blocked in production. ' +
        'Set VITE_FORM_SUBMITTER to a real adapter or VITE_ALLOW_CONSOLE_SUBMITTER=1.',
    );
  }
}

export function createSubmitter(id: SubmitterId): FormSubmitter {
  switch (id) {
    case 'console':
      return createConsoleSubmitter();
    case 'telegram':
    case 'bitrix':
    case 'email':
      return notImplemented(id);
  }
}

let cached: FormSubmitter | null = null;

/**
 * Возвращает singleton `FormSubmitter` по переменным окружения.
 *   - `VITE_FORM_SUBMITTER` → выбор адаптера (по умолчанию `console`);
 *   - `VITE_ALLOW_CONSOLE_SUBMITTER` → разрешает console в prod.
 */
export function getSubmitter(): FormSubmitter {
  if (cached) return cached;
  const id = resolveId(import.meta.env.VITE_FORM_SUBMITTER);
  enforceProductionGuard({
    id,
    isProd: Boolean(import.meta.env.PROD),
    allowConsole: Boolean(import.meta.env.VITE_ALLOW_CONSOLE_SUBMITTER),
  });
  cached = createSubmitter(id);
  return cached;
}

/**
 * Сбрасывает кэш (только для тестов).
 */
export function __resetSubmitterCache(): void {
  cached = null;
}

export type { FormSubmitter, LeadPayload, SubmitResult, LeadSource } from '@/lib/submitter/types';
export { NotImplementedError } from '@/lib/submitter/types';
